import type { DragEndEvent } from "@dnd-kit/core";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, TextField, Typography } from "@mui/material";
import { type Control, Controller, useFieldArray, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { LessonFormData } from "api/services/content/schemas/lessons";
import { SortableList } from "shared/atoms/DragAndDrop";

import { AnswersSection } from "./AnswersSection";
import type { SelectedScreen } from "./ScreenSelector";
import { SortableItem } from "./SortableItem";

interface Props {
  partIndex: number;
  control: Control<LessonFormData>;
  setSelectedScreen: React.Dispatch<React.SetStateAction<SelectedScreen>>;
}

export const QuestionsSection: React.FC<Props> = ({ partIndex, control, setSelectedScreen }) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: `parts.${partIndex}.translation.questions`,
  });

  const addQuestion = () => {
    append({
      title: "",
      answers: [{ text: "", correct: true }],
      tip_description: "",
    });
    setSelectedScreen({ screen: "quiz", index: fields.length });
  };

  const removeQuestion = (index: number) => {
    remove(index);
    if (index > 0) {
      setSelectedScreen({ screen: "quiz", index: index - 1 });
    } else {
      setSelectedScreen({ screen: "text_lesson", index: 0 });
    }
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id);
      const newIndex = fields.findIndex(field => field.id === over.id);
      move(oldIndex, newIndex);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("library.lessons.fields.questions")}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addQuestion} sx={{ ml: 2 }}>
          {t("buttons.add_question")}
        </Button>
      </Box>

      <SortableList items={fields.map(f => f.id)} onDragEnd={onDragEnd}>
        {fields.map((fieldItem, index) => (
          <StyledSortableItem
            key={fieldItem.id}
            fieldItem={fieldItem}
            index={index}
            $hasError={!!errors.parts?.[partIndex]?.translation?.questions?.[index]}
            hasMoreThanOne={fields.length > 1}
            label={`${t("library.lessons.fields.question")} ${index + 1}`}
            variant="body1"
            wrapperComponent="accordion"
            onRemove={() => removeQuestion(index)}
          >
            <Controller
              name={`parts.${partIndex}.translation.questions.${index}.title`}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("library.lessons.fields.question_title")}
                  fullWidth
                  margin="normal"
                  error={!!errors.parts?.[partIndex]?.translation?.questions?.[index]?.title}
                  helperText={
                    !!errors.parts?.[partIndex]?.translation?.questions?.[index]?.title && t("errors.field.required")
                  }
                />
              )}
            />

            <AnswersSection partIndex={partIndex} questionIndex={index} control={control} />

            <Controller
              name={`parts.${partIndex}.translation.questions.${index}.tip_description`}
              control={control}
              render={({ field }) => (
                <TextField {...field} label={t("library.lessons.fields.tip_description")} fullWidth margin="normal" />
              )}
            />
          </StyledSortableItem>
        ))}
      </SortableList>
    </Box>
  );
};

const StyledSortableItem = styled(SortableItem)<{ $hasError?: boolean }>`
  & > .MuiAccordion-root {
    margin-bottom: 12px !important;
    background: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.redesign.r10 : theme.colors.redesign.b5} !important;
    border: 1px solid ${({ theme, $hasError }) => ($hasError ? theme.colors.redesign.r70 : theme.colors.redesign.b70)};
    border-radius: 8px !important;

    &:not(:last-child) {
      border-bottom: 1px solid
        ${({ theme, $hasError }) => ($hasError ? theme.colors.redesign.r70 : theme.colors.redesign.b70)};
    }

    & .MuiAccordionDetails-root {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    & .MuiInputBase-root {
      background: ${({ theme }) => theme.colors.white};
    }
  }
`;
